import React, { useState, useEffect, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, subMonths, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    Search,
    RefreshCw,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    TrendingUp,
    Calendar,
    BarChart3,
    Users,
    Clock,
    UserX,
    HeartPulse,
    Timer,
    CalendarCheck,
    FileSpreadsheet,
    Building2,
    Trophy,
    AlertTriangle,
    FileText,
} from 'lucide-react';
import {
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ResponsiveContainer,
    LabelList,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.jsx';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import EmployeeCombobox from '@/pages/payroll/components/EmployeeCombobox';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { exportarControlExcel } from './controlExcel';
import AnalisisCentro from './AnalisisCentro';
import Destacados from './Destacados';
import Incumplidos from './Incumplidos';
import { TONOS, Pill, duracion, Kpi, Avatar, RANGOS } from './controlUi';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Estados del día con su color (claro y oscuro) y el grupo del filtro.
const ESTADOS = {
    Trabajando: { tono: 'verde', grupo: 'working' },
    'Trabajando (Extra)': { tono: 'violeta', grupo: 'working', texto: 'Trabajando · sin turno' },
    'Sin salida': { tono: 'naranja', grupo: 'working' },
    Pendiente: { tono: 'ambar', grupo: 'pending' },
    'No se presentó': { tono: 'rojo', grupo: 'absent' },
    Incapacidad: { tono: 'azul', grupo: 'leave' },
    Permiso: { tono: 'cian', grupo: 'leave' },
    Finalizado: { tono: 'gris', grupo: 'finished' },
    'Finalizado (Extra)': { tono: 'violeta', grupo: 'finished', texto: 'Finalizado · sin turno' },
    'Sin Turno': { tono: 'tenue', grupo: 'none', texto: 'Sin turno' },
};

const FILTROS_ESTADO = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'working', label: 'Trabajando' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'absent', label: 'No se presentó' },
    { value: 'leave', label: 'Incapacidad / permiso' },
    { value: 'finished', label: 'Finalizado' },
    { value: 'none', label: 'Sin turno' },
];

const EstadoPill = ({ status }) => {
    const e = ESTADOS[status] || { tono: 'gris' };
    return <Pill tono={e.tono}>{e.texto || status}</Pill>;
};

const textoAusencia = (a) =>
    a.tipo === 'incapacidad'
        ? `Incapacidad${a.origen ? ` (${a.origen === 'laboral' ? 'laboral' : 'común'})` : ''}`
        : `Permiso${a.remunerado ? '' : ' no remunerado'}`;

const AusenciaPill = ({ ausencia }) => (
    <Pill
        tono={ausencia.tipo === 'incapacidad' ? 'azul' : 'cian'}
        title={`${textoAusencia(ausencia)} del ${ausencia.fechaInicio} al ${ausencia.fechaFin}${ausencia.motivo ? ` · ${ausencia.motivo}` : ''}`}
    >
        <HeartPulse className="h-3 w-3" />
        {textoAusencia(ausencia)}
    </Pill>
);

const aMin = (hhmm) => {
    const m = String(hhmm || '').match(/^(\d{1,2}):(\d{2})/);
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

// Diferencia de salida real − planificada, la más corta alrededor de la medianoche.
const difSalida = (plan, real) => {
    const p = aMin(plan);
    const r = aMin(real);
    if (p == null || r == null) return null;
    let d = r - p;
    if (d > 720) d -= 1440;
    if (d < -720) d += 1440;
    return d;
};

/**
 * Salida real − salida planificada del día, en minutos. Si la salida se marcó otro día que la
 * llegada se suman esos días (una salida olvidada no debe verse como "salió antes").
 */
const difSalidaDia = (day) => {
    const w = day.worked;
    if (!day.schedule?.end || !w?.endTime) return null;
    const ini = aMin(day.schedule.start);
    let fin = aMin(day.schedule.end);
    const real = aMin(w.endTime);
    if (fin == null || real == null) return null;
    if (ini != null && fin <= ini) fin += 1440;
    if (!w.endDate || !w.startDate) return difSalida(day.schedule.end, w.endTime);
    const dias = Math.round((new Date(`${w.endDate}T12:00:00`) - new Date(`${w.startDate}T12:00:00`)) / 86400000);
    return dias * 1440 + real - fin;
};

const Retraso = ({ minutos }) => {
    if (minutos == null) return <span className="text-muted-foreground">—</span>;
    if (minutos === 0) return <span className="font-medium text-emerald-600 dark:text-emerald-400">A tiempo</span>;
    return (
        <span
            className={cn(
                'font-medium tabular-nums',
                minutos > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
            )}
            title={minutos > 0 ? 'Llegó tarde' : 'Llegó antes'}
        >
            {minutos > 0 ? '+' : '−'}
            {duracion(minutos)}
        </span>
    );
};

const RolTexto = ({ item }) =>
    item.assignment?.role ? (
        <span className="inline-flex items-center gap-1.5">
            {item.assignment.role.color && (
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.assignment.role.color }} />
            )}
            {item.assignment.role.nombre}
        </span>
    ) : (
        item.employee.rol || '—'
    );

const fechaCorta = (iso) => format(new Date(`${iso}T12:00:00`), 'dd/MM/yyyy');
const diasEntre = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000);

// Hora marcada con su fecha debajo. `desde`: fecha de la llegada, para avisar si la salida cayó otro día.
const Marca = ({ hora, fecha, desde }) => {
    if (!hora) return <span className="text-muted-foreground">—</span>;
    const dias = fecha && desde ? diasEntre(desde, fecha) : 0;
    return (
        <div className="leading-tight">
            <span className="font-medium tabular-nums">{hora}</span>
            {dias > 0 && (
                <Pill tono={dias > 1 ? 'rojo' : 'ambar'} className="ml-1 px-1.5 py-0" title="La salida se marcó otro día que la llegada">
                    +{dias} día{dias > 1 ? 's' : ''}
                </Pill>
            )}
            {fecha && <div className="text-xs text-muted-foreground tabular-nums">{fechaCorta(fecha)}</div>}
        </div>
    );
};

// Más de esto entre llegada y salida suele ser una salida olvidada.
const MAX_HORAS_TURNO = 16;

const HorasEjecutadas = ({ worked }) => {
    if (!worked) return <span className="text-muted-foreground">—</span>;
    if (!worked.endTime) return <span className="text-xs text-muted-foreground">En curso</span>;
    const revisar = worked.elapsedHours != null && worked.elapsedHours > MAX_HORAS_TURNO;
    return (
        <div className="leading-tight">
            <span className="font-medium tabular-nums">{worked.executedHours} h</span>
            {revisar && (
                <div
                    className="text-xs font-medium text-red-600 dark:text-red-400"
                    title={`Entre la llegada y la salida pasaron ${worked.elapsedHours} h: parece una salida olvidada.`}
                >
                    {Math.round(worked.elapsedHours)} h reales · revisar
                </div>
            )}
        </div>
    );
};

const HorasAutorizadas = ({ worked }) => {
    if (!worked?.endTime) return <span className="text-muted-foreground">—</span>;
    if (worked.authorizedHours == null) return <Pill tono="ambar">Sin autorizar</Pill>;
    const dif = Math.round((worked.authorizedHours - worked.executedHours) * 100) / 100;
    return (
        <div className="leading-tight">
            <span className="font-medium tabular-nums">{worked.authorizedHours} h</span>
            {!worked.authorized && <Pill tono="ambar" className="ml-1 px-1.5 py-0">parcial</Pill>}
            {(worked.authorizedStart || worked.authorizedEnd) && (
                <div className="text-xs text-muted-foreground tabular-nums">
                    {worked.authorizedStart || '?'} – {worked.authorizedEnd || '?'}
                </div>
            )}
            {Math.abs(dif) >= 0.25 && (
                <div
                    className={cn('text-xs tabular-nums', dif < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400')}
                    title="Diferencia entre las horas autorizadas y las marcadas"
                >
                    {dif > 0 ? '+' : ''}
                    {dif} h
                </div>
            )}
        </div>
    );
};

// "Lun 26 de oct": solo la primera letra en mayúscula.
const fechaLarga = (iso) => {
    const t = format(new Date(`${iso}T12:00:00`), "EEE d 'de' MMM", { locale: es });
    return t.charAt(0).toUpperCase() + t.slice(1);
};

// --------------------------------------------------------------------------- vista diaria

const VistaDiaria = ({ date, setDate, data, loading, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');
    const [laundryFilter, setLaundryFilter] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const rolDe = (item) => item.assignment?.role?.nombre || item.employee.rol || '';

    const uniqueRoles = useMemo(() => [...new Set(data.map(rolDe).filter(Boolean))].sort(), [data]);
    const uniqueLaundries = useMemo(
        () => [...new Set(data.map((i) => i.employee.lavanderia?.nombre).filter(Boolean))].sort(),
        [data],
    );

    const filas = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        const filtradas = data.filter((item) => {
            const nombre = `${item.employee.nombre} ${item.employee.apellido}`.toLowerCase();
            const matchesSearch = !q || nombre.includes(q) || String(item.employee.cedula || '').includes(q);
            const matchesStatus = statusFilter === 'all' || ESTADOS[item.status]?.grupo === statusFilter;
            const matchesRole = roleFilter === 'all' || rolDe(item) === roleFilter;
            const matchesLaundry = laundryFilter === 'all' || item.employee.lavanderia?.nombre === laundryFilter;
            return matchesSearch && matchesStatus && matchesRole && matchesLaundry;
        });
        if (!sortConfig.key) return filtradas;
        const valor = (i) => {
            switch (sortConfig.key) {
                case 'nombre':
                    return `${i.employee.nombre} ${i.employee.apellido}`.toLowerCase();
                case 'lavanderia':
                    return i.employee.lavanderia?.nombre || '';
                case 'rol':
                    return rolDe(i);
                case 'entrada':
                    return i.worked?.startTime || '';
                case 'salida':
                    return `${i.worked?.endDate || ''} ${i.worked?.endTime || ''}`;
                case 'ejecutadas':
                    return i.worked?.executedHours ?? -1;
                case 'autorizadas':
                    return i.worked?.authorizedHours ?? -1;
                case 'retraso':
                    return i.delay?.minutes ?? -99999;
                case 'estado':
                    return i.status;
                default:
                    return '';
            }
        };
        const dir = sortConfig.direction === 'asc' ? 1 : -1;
        return [...filtradas].sort((a, b) => (valor(a) < valor(b) ? -dir : valor(a) > valor(b) ? dir : 0));
    }, [data, searchTerm, statusFilter, roleFilter, laundryFilter, sortConfig]);

    const conteo = (grupo) => data.filter((d) => ESTADOS[d.status]?.grupo === grupo).length;
    const programados = data.filter((d) => d.schedule).length;
    const tarde = data.filter((d) => (d.delay?.minutes ?? 0) > 5).length;

    const handleSort = (key) =>
        setSortConfig((s) => ({ key, direction: s.key === key && s.direction === 'asc' ? 'desc' : 'asc' }));
    const Orden = ({ k, children, className }) => (
        <TableHead className={cn('cursor-pointer select-none whitespace-nowrap hover:text-foreground', className)} onClick={() => handleSort(k)}>
            {children}
            {sortConfig.key !== k ? (
                <ArrowUpDown className="ml-1 inline h-3.5 w-3.5 opacity-50" />
            ) : sortConfig.direction === 'asc' ? (
                <ArrowUp className="ml-1 inline h-3.5 w-3.5" />
            ) : (
                <ArrowDown className="ml-1 inline h-3.5 w-3.5" />
            )}
        </TableHead>
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-2">
                <div className="space-y-1">
                    <Label className="text-xs">Fecha</Label>
                    <Input
                        type="date"
                        value={date}
                        onChange={(e) => e.target.value && setDate(e.target.value)}
                        className="w-44"
                    />
                </div>
                <Button variant="outline" size="icon" onClick={onRefresh} disabled={loading} title="Actualizar">
                    <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
                </Button>
                <p className="pb-2 text-sm text-muted-foreground">{fechaLarga(date)}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <Kpi icon={Users} label="Personal activo" value={data.length} sub={`${programados} con turno`} />
                <Kpi icon={Clock} label="Trabajando" value={conteo('working')} tone="text-emerald-600 dark:text-emerald-400" />
                <Kpi icon={Timer} label="Pendientes" value={conteo('pending')} sub="Su turno aún no empieza" tone="text-amber-600 dark:text-amber-400" />
                <Kpi icon={UserX} label="No se presentaron" value={conteo('absent')} tone={conteo('absent') ? 'text-red-600 dark:text-red-400' : undefined} />
                <Kpi icon={HeartPulse} label="Incapacidad / permiso" value={data.filter((d) => d.ausencia).length} tone="text-sky-600 dark:text-sky-400" />
                <Kpi icon={Clock} label="Llegaron tarde" value={tarde} sub="Más de 5 min" tone={tarde ? 'text-red-600 dark:text-red-400' : undefined} />
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <CardTitle className="text-base">Listado de personal</CardTitle>
                            <CardDescription>{filas.length} de {data.length} personas</CardDescription>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar por nombre o cédula…"
                                    className="w-full pl-8 sm:w-64"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="sm:w-48"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {FILTROS_ESTADO.map((f) => (
                                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={roleFilter} onValueChange={setRoleFilter}>
                                <SelectTrigger className="sm:w-44"><SelectValue placeholder="Rol" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los roles</SelectItem>
                                    {uniqueRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Select value={laundryFilter} onValueChange={setLaundryFilter}>
                                <SelectTrigger className="sm:w-52"><SelectValue placeholder="Centro" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los centros</SelectItem>
                                    {uniqueLaundries.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="overflow-x-auto rounded-md border">
                        <Table className="[&_td]:py-2">
                            <TableHeader>
                                <TableRow className="bg-muted/40 hover:bg-muted/40">
                                    <Orden k="nombre">Empleado</Orden>
                                    <Orden k="lavanderia">Centro</Orden>
                                    <Orden k="rol">Rol</Orden>
                                    <TableHead className="whitespace-nowrap">Turno planificado</TableHead>
                                    <Orden k="entrada">Entrada</Orden>
                                    <Orden k="salida">Salida</Orden>
                                    <Orden k="retraso">Retraso / adelanto</Orden>
                                    <Orden k="ejecutadas" className="text-right">Ejecutadas</Orden>
                                    <Orden k="autorizadas" className="text-right">Autorizadas</Orden>
                                    <Orden k="estado">Estado</Orden>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && !data.length ? (
                                    <TableRow>
                                        <TableCell colSpan={10} className="h-24 text-center text-muted-foreground">Cargando…</TableCell>
                                    </TableRow>
                                ) : filas.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} className="h-24 text-center text-muted-foreground">No se encontraron registros.</TableCell>
                                    </TableRow>
                                ) : (
                                    filas.map((item) => (
                                        <TableRow key={item.employee.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <Avatar emp={item.employee} />
                                                    <div className="min-w-0">
                                                        <div className="font-medium leading-tight">{item.employee.nombre} {item.employee.apellido}</div>
                                                        <div className="text-xs text-muted-foreground">{item.employee.cedula || 'Sin cédula'}</div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {item.employee.lavanderia?.nombre || <span className="text-muted-foreground">—</span>}
                                                {item.worked?.center && item.worked.center !== item.employee.lavanderia?.nombre && (
                                                    <div className="text-xs text-muted-foreground">Marcó en {item.worked.center}</div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm"><RolTexto item={item} /></TableCell>
                                            <TableCell>
                                                {item.schedule ? (
                                                    <div>
                                                        <span className="font-medium tabular-nums">{item.schedule.start} – {item.schedule.end}</span>
                                                        {item.assignment?.scheduleType?.nombre && (
                                                            <div className="text-xs text-muted-foreground">{item.assignment.scheduleType.nombre}</div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-sm text-muted-foreground">No planificado</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Marca hora={item.worked?.startTime} fecha={item.worked?.startDate} />
                                                {item.worked?.records > 1 && (
                                                    <div className="text-xs text-muted-foreground">{item.worked.records} marcaciones</div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Marca hora={item.worked?.endTime} fecha={item.worked?.endDate} desde={item.worked?.startDate} />
                                            </TableCell>
                                            <TableCell><Retraso minutos={item.delay?.minutes} /></TableCell>
                                            <TableCell className="text-right"><HorasEjecutadas worked={item.worked} /></TableCell>
                                            <TableCell className="text-right"><HorasAutorizadas worked={item.worked} /></TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap gap-1">
                                                    <EstadoPill status={item.status} />
                                                    {item.ausencia && item.worked && <AusenciaPill ausencia={item.ausencia} />}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

// --------------------------------------------------------------------------- análisis histórico

const observacionDia = (day) => {
    const notas = [];
    if (day.ausencia) {
        notas.push({
            tono: day.ausencia.tipo === 'incapacidad' ? 'azul' : 'cian',
            texto: day.worked ? `${textoAusencia(day.ausencia)} registrada, pero marcó` : textoAusencia(day.ausencia),
            title: day.ausencia.motivo,
        });
    }
    if (!day.schedule && day.worked) notas.push({ tono: 'violeta', texto: 'Trabajó sin turno planificado' });
    if (day.worked?.records > 1) notas.push({ tono: 'gris', texto: `${day.worked.records} marcaciones` });
    if (day.worked && !day.worked.authorized && day.worked.endTime) notas.push({ tono: 'ambar', texto: 'Horas sin autorizar' });
    return notas;
};

// Minutos sobre cada barra: arriba si llegó tarde (rojo), debajo si llegó antes.
const EtiquetaMinutos = ({ x, y, width, height, value }) => {
    if (value == null || value === 0) return null;
    const tarde = value > 0;
    const cx = x + width / 2;
    // En barras negativas recharts entrega y en la base y height negativo o positivo según la versión.
    const borde = tarde ? Math.min(y, y + height) - 4 : Math.max(y, y + height) + 11;
    return (
        <text
            x={cx}
            y={borde}
            textAnchor="middle"
            fontSize={10}
            fontWeight={tarde ? 700 : 500}
            fill={tarde ? '#dc2626' : '#059669'}
        >
            {tarde ? `+${value}` : value}
        </text>
    );
};

const TooltipDia = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
        <div className="rounded-md border bg-popover p-3 text-sm text-popover-foreground shadow-md">
            <p className="font-semibold">{fechaLarga(d.fecha)}</p>
            <p className="text-muted-foreground">Planificado: {d.plan}</p>
            <p className="text-muted-foreground">Entrada: {d.real}</p>
            <p className={cn('mt-1 font-medium', d.minutos > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400')}>
                {d.minutos > 0 ? `Llegó ${duracion(d.minutos)} tarde` : d.minutos < 0 ? `Llegó ${duracion(d.minutos)} antes` : 'Puntual'}
            </p>
        </div>
    );
};

const AnalisisHistorico = ({ employees, solicitud }) => {
    const { toast } = useToast();
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generandoPdf, setGenerandoPdf] = useState(false);

    const descargarExcel = () =>
        exportarControlExcel(resultado).catch((e) =>
            toast({ title: 'No se exportó', description: e.message, variant: 'destructive' }),
        );

    // PDF para entregar al empleado: lo arma el backend con el logo y los datos de la empresa.
    const descargarPdf = async () => {
        try {
            setGenerandoPdf(true);
            const response = await axios.get(`${API_URL}/shifts/control/historical/pdf`, {
                params: { startDate: resultado.startDate, endDate: resultado.endDate, employeeId: resultado.employee.employee.id },
                responseType: 'blob',
            });
            const e = resultado.employee.employee;
            const persona = `${e.nombre} ${e.apellido}`
                .trim()
                .normalize('NFD')
                .replace(/[̀-ͯ]/g, '')
                .replace(/[^A-Za-z0-9]+/g, '_');
            saveAs(response.data, `control-personal_${persona}_${resultado.startDate}_a_${resultado.endDate}.pdf`);
        } catch (error) {
            console.error('Error generando el PDF:', error);
            toast({ title: 'Error', description: 'No se pudo generar el reporte PDF.', variant: 'destructive' });
        } finally {
            setGenerandoPdf(false);
        }
    };

    const generar = async (pedido = {}) => {
        const empleadoId = pedido.employeeId || selectedEmployee;
        const desde = pedido.startDate || startDate;
        const hasta = pedido.endDate || endDate;
        if (!empleadoId) return;
        if (hasta < desde) {
            toast({ title: 'Rango no válido', description: 'La fecha fin es anterior a la de inicio.', variant: 'destructive' });
            return;
        }
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/shifts/control/historical`, {
                params: { startDate: desde, endDate: hasta, employeeId: empleadoId },
            });
            setResultado({ ...response.data, employee: response.data?.employees?.[0] || null });
        } catch (error) {
            console.error('Error fetching historical control:', error);
            toast({
                title: 'Error',
                description: error?.response?.data?.message || 'No se pudo cargar el análisis histórico.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    // «Ver detalle» desde el análisis por centro: carga esa persona y ese rango.
    useEffect(() => {
        if (!solicitud) return;
        setSelectedEmployee(solicitud.employeeId);
        setStartDate(solicitud.startDate);
        setEndDate(solicitud.endDate);
        generar(solicitud);
    }, [solicitud]);

    // Cambiar el empleado o el rango invalida el reporte anterior.
    const vigente =
        resultado &&
        resultado.startDate === startDate &&
        resultado.endDate === endDate &&
        (!resultado.employee || resultado.employee.employee.id === selectedEmployee);
    useEffect(() => {
        if (resultado && !vigente) setResultado(null);
    }, [vigente]);

    const emp = resultado?.employee;
    const st = emp?.statistics;
    const diasConDatos = useMemo(
        () => (emp ? emp.days.filter((d) => d.schedule || d.worked || d.ausencia) : []),
        [emp],
    );
    const serie = useMemo(
        () =>
            (emp?.days || [])
                .filter((d) => d.delay.minutes != null)
                .map((d) => ({
                    fecha: d.date,
                    etiqueta: format(new Date(`${d.date}T12:00:00`), 'dd/MM'),
                    minutos: d.delay.minutes,
                    plan: d.schedule?.start,
                    real: d.worked?.startTime,
                })),
        [emp],
    );

    return (
        <div className="space-y-4">
            <Card>
                <CardContent className="p-4">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_repeat(2,minmax(0,1fr))_auto] md:items-end">
                        <div className="space-y-1">
                            <Label className="text-xs">Empleado</Label>
                            <EmployeeCombobox
                                employees={employees}
                                value={selectedEmployee}
                                onValueChange={(e) => setSelectedEmployee(e?._id || null)}
                                placeholder="Busca por nombre o cédula…"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Desde</Label>
                            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Hasta</Label>
                            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </div>
                        <Button onClick={() => generar()} disabled={loading || !selectedEmployee}>
                            {loading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <BarChart3 className="mr-2 h-4 w-4" />}
                            {loading ? 'Generando…' : 'Generar reporte'}
                        </Button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {RANGOS.map((r) => (
                            <Button
                                key={r.label}
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => {
                                    const [a, b] = r.calc();
                                    setStartDate(format(a, 'yyyy-MM-dd'));
                                    setEndDate(format(b, 'yyyy-MM-dd'));
                                }}
                            >
                                {r.label}
                            </Button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {!resultado ? (
                <Card>
                    <CardContent className="flex h-56 flex-col items-center justify-center text-center">
                        <BarChart3 className="mb-3 h-10 w-10 text-muted-foreground/60" />
                        <p className="text-muted-foreground">Selecciona un empleado y el rango, y genera el reporte de asistencia y puntualidad.</p>
                    </CardContent>
                </Card>
            ) : !emp ? (
                <Card>
                    <CardContent className="flex h-40 items-center justify-center text-muted-foreground">
                        No hay turnos, marcaciones ni incapacidades para el empleado en ese rango.
                    </CardContent>
                </Card>
            ) : (
                <>
                    <Card>
                        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                            <Avatar emp={emp.employee} size="h-14 w-14" />
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold">{emp.employee.nombre} {emp.employee.apellido}</h3>
                                <p className="text-sm text-muted-foreground">
                                    CC {emp.employee.cedula || '—'} · {emp.employee.lavanderia?.nombre || 'Sin centro'} · <RolTexto item={emp} />
                                    {emp.employee.estado && emp.employee.estado !== 'Activo' && ` · ${emp.employee.estado}`}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Del {format(new Date(`${resultado.startDate}T12:00:00`), "d 'de' MMM yyyy", { locale: es })} al{' '}
                                    {format(new Date(`${resultado.endDate}T12:00:00`), "d 'de' MMM yyyy", { locale: es })}
                                    {resultado.endDate > resultado.today && ' · los días futuros solo muestran el turno planificado'}
                                </p>
                            </div>
                            <div className="sm:text-right">
                                <p className="text-xs uppercase tracking-wide text-muted-foreground">Balance de llegada</p>
                                <p
                                    className={cn(
                                        'text-2xl font-semibold tabular-nums',
                                        st.balanceMinutes > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400',
                                    )}
                                >
                                    {st.balanceMinutes > 0 ? '+' : st.balanceMinutes < 0 ? '−' : ''}
                                    {duracion(st.balanceMinutes)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {st.balanceMinutes > 0 ? 'En contra (llegadas tarde)' : 'A favor (llegadas antes)'}
                                </p>
                            </div>
                            <div className="flex gap-2 sm:flex-col">
                                <Button variant="outline" size="sm" onClick={descargarExcel}>
                                    <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
                                </Button>
                                <Button size="sm" onClick={descargarPdf} disabled={generandoPdf}>
                                    {generandoPdf ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                                    {generandoPdf ? 'Generando…' : 'Reporte PDF'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                        <Kpi
                            icon={CalendarCheck}
                            label="Asistencia"
                            value={st.attendanceRate == null ? '—' : `${st.attendanceRate} %`}
                            sub={`${st.plannedWorkedDays} de ${st.exigibleDays} turnos cumplidos`}
                            tone={st.attendanceRate != null && st.attendanceRate < 90 ? 'text-red-600 dark:text-red-400' : undefined}
                        />
                        <Kpi
                            icon={Clock}
                            label="Puntualidad"
                            value={st.punctualityRate == null ? '—' : `${st.punctualityRate} %`}
                            sub={`${st.onTimeDays} a tiempo · ${st.lateDays} tarde`}
                            tone={st.punctualityRate != null && st.punctualityRate < 85 ? 'text-amber-600 dark:text-amber-400' : undefined}
                        />
                        <Kpi icon={Timer} label="Minutos tarde" value={duracion(st.lateMinutes)} sub={`Promedio ${st.avgDelayMinutes > 0 ? '+' : ''}${st.avgDelayMinutes} min por llegada`} />
                        <Kpi
                            icon={UserX}
                            label="No se presentó"
                            value={st.absentDays}
                            sub={st.pendingDays ? `${st.pendingDays} turnos por venir` : 'Turnos sin marcación'}
                            tone={st.absentDays ? 'text-red-600 dark:text-red-400' : undefined}
                        />
                        <Kpi
                            icon={HeartPulse}
                            label="Incapacidad / permiso"
                            value={st.incapacityDays + st.permissionDays}
                            sub={`${st.incapacityDays} de incapacidad · ${st.permissionDays} de permiso`}
                            tone="text-sky-600 dark:text-sky-400"
                        />
                        <Kpi
                            icon={Users}
                            label="Horas ejecutadas"
                            value={`${st.executedHours} h`}
                            sub={`${st.authorizedHours} h autorizadas${st.pendingAuthorizationDays ? ` · ${st.pendingAuthorizationDays} días sin autorizar` : ''} · ${st.workedDays} días`}
                        />
                    </div>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">Puntualidad diaria</CardTitle>
                            <CardDescription>Minutos de diferencia entre la entrada marcada y la planificada: arriba de 0 llegó tarde, abajo llegó antes.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {serie.length === 0 ? (
                                <div className="flex h-48 items-center justify-center text-muted-foreground">
                                    No hay días con turno planificado y marcación en el rango.
                                </div>
                            ) : (
                                <>
                                <div className="mb-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="h-2.5 w-2.5 rounded-sm bg-red-500" /> Llegó tarde (+ minutos)
                                    </span>
                                    <span className="inline-flex items-center gap-1.5">
                                        <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Llegó antes o a tiempo (− minutos)
                                    </span>
                                    <span className="ml-auto">
                                        Tarde: <b className="text-red-600 dark:text-red-400">{serie.filter((d) => d.minutos > 0).length} días · {duracion(serie.reduce((s, d) => s + Math.max(0, d.minutos), 0))}</b>
                                    </span>
                                </div>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={serie} margin={{ top: 20, right: 8, left: 0, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
                                        <XAxis dataKey="etiqueta" fontSize={11} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                                        <YAxis fontSize={11} tickLine={false} axisLine={false} width={40} />
                                        <Tooltip content={<TooltipDia />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }} />
                                        <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" />
                                        <Bar dataKey="minutos" radius={[3, 3, 3, 3]} maxBarSize={28}>
                                            {serie.map((d) => (
                                                <Cell key={d.fecha} fill={d.minutos > 0 ? '#ef4444' : '#10b981'} />
                                            ))}
                                            <LabelList dataKey="minutos" content={<EtiquetaMinutos />} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base">Detalle por día</CardTitle>
                            <CardDescription>{diasConDatos.length} días con turno, marcación, incapacidad o permiso.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto rounded-md border">
                                <Table className="[&_td]:py-2">
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableHead>Fecha</TableHead>
                                            <TableHead>Turno</TableHead>
                                            <TableHead>Entrada</TableHead>
                                            <TableHead>Llegada</TableHead>
                                            <TableHead>Salida</TableHead>
                                            <TableHead>Salida vs. plan</TableHead>
                                            <TableHead className="text-right">Ejecutadas</TableHead>
                                            <TableHead className="text-right">Autorizadas</TableHead>
                                            <TableHead>Estado</TableHead>
                                            <TableHead>Observación</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {diasConDatos.map((day) => {
                                            const salida = difSalidaDia(day);
                                            return (
                                                <TableRow key={day.date} className={cn(day.status === 'No se presentó' && 'bg-red-500/5')}>
                                                    <TableCell className="whitespace-nowrap font-medium">{fechaLarga(day.date)}</TableCell>
                                                    <TableCell className="whitespace-nowrap tabular-nums">
                                                        {day.schedule ? `${day.schedule.start} – ${day.schedule.end}` : <span className="text-muted-foreground">—</span>}
                                                    </TableCell>
                                                    <TableCell><Marca hora={day.worked?.startTime} fecha={day.worked?.startDate} /></TableCell>
                                                    <TableCell><Retraso minutos={day.delay.minutes} /></TableCell>
                                                    <TableCell><Marca hora={day.worked?.endTime} fecha={day.worked?.endDate} desde={day.worked?.startDate} /></TableCell>
                                                    <TableCell>
                                                        {salida == null ? (
                                                            <span className="text-muted-foreground">—</span>
                                                        ) : salida === 0 ? (
                                                            <span className="text-muted-foreground">A la hora</span>
                                                        ) : (
                                                            <span
                                                                className={cn(
                                                                    'tabular-nums',
                                                                    salida < 0 ? 'font-medium text-red-600 dark:text-red-400' : 'text-muted-foreground',
                                                                )}
                                                            >
                                                                {salida > 0 ? `+${duracion(salida)} después` : `${duracion(salida)} antes`}
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right"><HorasEjecutadas worked={day.worked} /></TableCell>
                                                    <TableCell className="text-right"><HorasAutorizadas worked={day.worked} /></TableCell>
                                                    <TableCell><EstadoPill status={day.status} /></TableCell>
                                                    <TableCell>
                                                        <div className="flex flex-wrap gap-1">
                                                            {observacionDia(day).map((n) => (
                                                                <Pill key={n.texto} tono={n.tono} title={n.title}>{n.texto}</Pill>
                                                            ))}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                            <p className="mt-3 text-xs text-muted-foreground">
                                Días en hora de Bogotá. «Llegada» compara la primera entrada del día con el inicio del turno planificado
                                (todas las asignaciones de la semana). Los días con incapacidad o permiso registrados no cuentan como
                                inasistencia. Las horas son las autorizadas o, si aún no se autorizan, las marcadas.
                            </p>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

// --------------------------------------------------------------------------- página

const EmployeeControlPage = () => {
    const { toast } = useToast();
    const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [activeTab, setActiveTab] = useState('daily');
    const [solicitudHistorico, setSolicitudHistorico] = useState(null);

    const verPersona = (employeeId, startDate, endDate) => {
        setSolicitudHistorico({ employeeId, startDate, endDate, n: Date.now() });
        setActiveTab('historical');
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/shifts/control/daily`, { params: { date } });
            setData(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching daily control:', error);
            toast({
                title: 'Error',
                description: 'No se pudo cargar la información de control de empleados.',
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [date]);

    // Personas activas para el selector del análisis histórico.
    const empleados = useMemo(
        () =>
            data
                .map((i) => ({
                    _id: i.employee.id,
                    nombre: i.employee.nombre,
                    apellido: i.employee.apellido,
                    cedula: i.employee.cedula,
                    rol: i.employee.rol,
                    lavanderia: i.employee.lavanderia,
                }))
                .sort((a, b) => `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`)),
        [data],
    );

    return (
        <div className="container mx-auto space-y-6 py-6">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Control de personal</h1>
                <p className="text-sm text-muted-foreground">
                    Asistencia del día contra la planificación de turnos, incapacidades y permisos, y análisis de puntualidad por persona.
                </p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="daily" className="gap-2">
                        <Calendar className="h-4 w-4" /> Vista diaria
                    </TabsTrigger>
                    <TabsTrigger value="center" className="gap-2">
                        <Building2 className="h-4 w-4" /> Análisis por centro
                    </TabsTrigger>
                    <TabsTrigger value="highlights" className="gap-2">
                        <Trophy className="h-4 w-4" /> Destacados
                    </TabsTrigger>
                    <TabsTrigger value="offenders" className="gap-2">
                        <AlertTriangle className="h-4 w-4" /> Incumplidos
                    </TabsTrigger>
                    <TabsTrigger value="historical" className="gap-2">
                        <TrendingUp className="h-4 w-4" /> Análisis histórico
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="daily" className="mt-4">
                    <VistaDiaria date={date} setDate={setDate} data={data} loading={loading} onRefresh={fetchData} />
                </TabsContent>

                <TabsContent value="center" className="mt-4">
                    <AnalisisCentro onVerPersona={verPersona} />
                </TabsContent>

                <TabsContent value="highlights" className="mt-4">
                    <Destacados onVerPersona={verPersona} />
                </TabsContent>

                <TabsContent value="offenders" className="mt-4">
                    <Incumplidos onVerPersona={verPersona} />
                </TabsContent>

                <TabsContent value="historical" className="mt-4">
                    <AnalisisHistorico employees={empleados} solicitud={solicitudHistorico} />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default EmployeeControlPage;
